<script setup lang="ts">
import {ProductSummary} from "@/product/interfaces/ProductSummary";
import {Skeleton} from "@/core/components/ui/skeleton";
import ConsumerProductChip from "@/drug-interaction/components/header/ConsumerProductChip.vue";
import {computed, ref} from 'vue';
import {Popover, PopoverTrigger, PopoverContent} from "@/core/components/ui/popover";
import {Button} from "@/core/components/ui/button";

defineProps<{
    products?: ProductSummary[];
    isLoading: boolean;
}>();

const emit = defineEmits<{
    (e: 'openDosageDialog', product: ProductSummary): void;
    (e: 'trackProducts'): void;
}>();

const container = ref<HTMLElement | null>(null);
const chipRefs = ref<(HTMLElement | null)[]>([]);
const hoveredIndex = ref<number>(-1);
const expandDirection = ref<'left' | 'right'>('left');

const setChipRef = (el: HTMLElement | null, index: number): void => {
    if (el) {
        while (chipRefs.value.length <= index) {
            chipRefs.value.push(null);
        }
        chipRefs.value[index] = el;
    }
};

const expandedPositionClass = computed(() => {
    return expandDirection.value === 'right' ? 'right-0' : 'left-0';
});

const handleHover = (index: number) => {
    hoveredIndex.value = index;

    const chipEl = chipRefs.value[index];
    if (chipEl && container.value) {
        const chipRect = chipEl.getBoundingClientRect();
        const containerRect = container.value.getBoundingClientRect();

        if (chipRect.right + 300 > containerRect.right) {
            expandDirection.value = 'right';
        } else {
            expandDirection.value = 'left';
        }
    }
};

const handleLeave = () => {
    hoveredIndex.value = -1;
};
</script>

<template>
  <div v-if="isLoading" class="flex gap-2">
    <Skeleton v-for="i in 2" :key="i" class="w-[150px] rounded-full h-6" />
  </div>
  <div v-else>
    <div v-if="!products?.length" class="text-sm">
      Aucun traitement enregistré
    </div>
    <div v-else>
      <div ref="container" class="flex gap-2 flex-wrap max-w-[650px]">
        <div
          v-for="(product, index) in products"
          :key="product.id"
          :ref="(el) => setChipRef(el as HTMLElement, index)"
          class="relative group"
          @mouseenter="handleHover(index)"
          @mouseleave="handleLeave"
        >
          <Popover>
            <PopoverTrigger>
              <ConsumerProductChip
                class="max-w-[150px]"
                :class="{ 'opacity-0': hoveredIndex === index }"
                :index="index"
                :product="product"
              />

              <div
                v-if="hoveredIndex === index"
                class="absolute top-0 z-20 transition-opacity duration-300 cursor-pointer"
                :class="expandedPositionClass"
              >
                <ConsumerProductChip
                  class="max-w-[650px]"
                  :index="index"
                  :product="product"
                />
              </div>
            </PopoverTrigger>
            <PopoverContent class="p-0 w-auto">
              <Button variant="ghost" class="w-full text-start" @click="emit('openDosageDialog', product)">
                Plan de posologie
              </Button>
              <Button
                variant="ghost" class="w-full text-start" @click="emit('trackProducts')"
              >
                Suivi des traitements
              </Button>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  </div>
</template>
